import { Directive, ElementRef, Input, NgZone, OnInit, SimpleChanges } from '@angular/core';

import { fromEvent as observableFromEvent, ReplaySubject, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

export type AvatarThemeBackground =
  | 'background-white'
  | 'background-dark'
  | 'background-dark-dropdown'
  | 'background-light'
  | 'background-private'
  | 'background-private-darker'
  | 'background-infobox'
  | 'background-default'
  | 'background-dark-dropdown-hover'
  | 'background-table-row-dark';

export type AvatarThemeClassNames = { idle: AvatarThemeBackground; hover?: AvatarThemeBackground };

export class AvatarThemeSubject extends ReplaySubject<string | null> {}

export function avatarThemeFactory(): AvatarThemeSubject {
  return new AvatarThemeSubject(1);
}

@Directive({
  selector: '[rtAvatarTheme]',
  providers: [
    {
      provide: AvatarThemeSubject,
      useFactory: avatarThemeFactory,
    },
  ],
})
export class AvatarThemeDirective implements OnInit {
  @Input('rtAvatarTheme') public theme: AvatarThemeClassNames;
  @Input('rtAvatarThemeActive') public active: string | null = null;

  private ngOnDestroy$: Subject<void> = new Subject();
  private currentThemeClassKey: keyof AvatarThemeClassNames;

  constructor(
    private elRef: ElementRef,
    private ngZone: NgZone,
    private theme$: AvatarThemeSubject,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if ('theme' in changes && !this.active) {
      this.setThemeClass(this.currentThemeClassKey);
    }

    if ('active' in changes) {
      if (changes.active.currentValue === null) {
        this.setThemeClass(this.currentThemeClassKey);
      } else if (typeof changes.active.currentValue === 'string') {
        this.theme$.next(this.active);
      }
    }
  }

  public ngOnInit(): void {
    if (this.active) {
      this.theme$.next(this.active);
    } else {
      this.setThemeClass('idle');
    }

    this.ngZone.runOutsideAngular(() =>
      observableFromEvent(this.elRef.nativeElement, 'mouseenter')
        .pipe(
          filter(
            () => this.theme && this.theme.hover !== undefined && this.theme.idle !== undefined,
          ),
          tap(() => this.setThemeClass('hover')),
          switchMap(() => observableFromEvent(this.elRef.nativeElement, 'mouseleave')),
          tap(() => {
            if (!this.active) {
              this.setThemeClass('idle');
            }
          }),
          takeUntil(this.ngOnDestroy$.asObservable()),
        )
        .subscribe(),
    );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private setThemeClass(classNameKey: keyof AvatarThemeClassNames): void {
    this.currentThemeClassKey = classNameKey;
    this.theme$.next(this.theme[this.currentThemeClassKey]);
  }
}
